/**
 * Iter XX.187 — Founder Transparency: Mini Aha Trace.
 *
 * Founder-only observational debug surface for the XX.186 mini_aha
 * engine. For any conversation / user / browser, this page shows:
 *   · session_id + turn number
 *   · eligibility (fired vs skipped)
 *   · aha type (reframe · actionable_cue · memory_callback ·
 *     pattern_spotted)
 *   · skip reason (when not fired)
 *   · triggering evidence / markers (substrate)
 *   · the generated insight block that reached the LLM
 *   · rate-limit state (via user_turns_so_far since last fire)
 *   · adjacent user message + Gully response so the aha can be judged
 *     in context
 *
 * Filters: aha_type · fired/skipped · skip_reason · date range.
 *
 * NO CONTROLS on this panel modify production behaviour. Read-only.
 * Server enforces admin auth; this page just fetches and renders.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";

const AHA_TYPES = [
  { value: "", label: "any type" },
  { value: "reframe", label: "reframe" },
  { value: "actionable_cue", label: "actionable_cue" },
  { value: "memory_callback", label: "memory_callback" },
  { value: "pattern_spotted", label: "pattern_spotted" },
];

const FIRED_STATES = [
  { value: "", label: "any" },
  { value: "true", label: "fired" },
  { value: "false", label: "skipped" },
];

const SKIP_REASONS = [
  "",
  "anonymous",
  "first_two_turns",
  "distress_active",
  "clarification_lock",
  "billing_flow_active",
  "injury_context_active",
  "upload_in_progress",
  "outcome_boundary_reached",
  "dam_owns_this_turn",
  "recently_fired",
  "no_substrate",
  "semantic_repeat",
];

function Chip({ tone = "neutral", children, testId }) {
  const tones = {
    fired: "bg-emerald-900/60 text-emerald-200 border-emerald-800",
    skipped: "bg-neutral-900/70 text-neutral-400 border-neutral-800",
    reframe: "bg-indigo-900/60 text-indigo-200 border-indigo-800",
    actionable_cue: "bg-amber-900/60 text-amber-200 border-amber-800",
    memory_callback: "bg-cyan-900/60 text-cyan-200 border-cyan-800",
    pattern_spotted: "bg-fuchsia-900/60 text-fuchsia-200 border-fuchsia-800",
    neutral: "bg-neutral-900/60 text-neutral-300 border-neutral-800",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider ${
        tones[tone] || tones.neutral
      }`}
    >
      {children}
    </span>
  );
}

function EvidenceBlock({ substrate }) {
  if (!substrate || typeof substrate !== "object") return null;
  const entries = Object.entries(substrate);
  if (!entries.length) return null;
  return (
    <div
      data-testid="mini-aha-substrate"
      className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono"
    >
      {entries.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-2">
          <span className="text-neutral-500">{k}</span>
          <span className="text-neutral-300 text-right truncate max-w-[70%]">
            {typeof v === "object" ? JSON.stringify(v) : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}

function TraceCard({ r, idx }) {
  const [rawOpen, setRawOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const firedTone = r.fired ? "fired" : "skipped";
  const typeTone = r.fired ? r.aha_type || "neutral" : "skipped";
  const rateHint =
    typeof r.user_turns_so_far === "number"
      ? `turn ${r.user_turns_so_far}`
      : "turn ?";
  return (
    <div
      data-testid={`mini-aha-card-${idx}`}
      className="border border-neutral-800 rounded-lg p-4 bg-neutral-950"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Chip tone={firedTone} testId={`mini-aha-status-${idx}`}>
            {r.fired ? "fired" : "skipped"}
          </Chip>
          {r.fired ? (
            <Chip tone={typeTone} testId={`mini-aha-type-${idx}`}>
              {r.aha_type || "unknown"}
            </Chip>
          ) : (
            <Chip tone="neutral" testId={`mini-aha-reason-${idx}`}>
              {r.skip_reason || "unknown"}
            </Chip>
          )}
          {r.fired && r.progression_type ? (
            <Chip tone="neutral" testId={`mini-aha-progression-${idx}`}>
              {r.progression_type}
            </Chip>
          ) : null}
          <span className="text-[11px] text-neutral-500 font-mono">
            {rateHint}
          </span>
        </div>
        <div
          data-testid={`mini-aha-meta-${idx}`}
          className="text-[10px] text-neutral-500 font-mono"
        >
          {r.fired_at || "?"} · session_index {r.session_index ?? "?"}
        </div>
      </div>

      <div className="text-[11px] text-neutral-500 font-mono mb-2 break-all">
        session: {r.session_id || "—"} · user: {r.user_id || "—"}
      </div>

      {r.preceding_user_message ? (
        <div
          data-testid={`mini-aha-user-msg-${idx}`}
          className="text-xs italic text-neutral-400 border-l-2 border-neutral-800 pl-3 mb-2"
        >
          &ldquo;{r.preceding_user_message}&rdquo;
        </div>
      ) : null}

      <EvidenceBlock substrate={r.substrate} />

      {r.following_gully_response ? (
        <div
          data-testid={`mini-aha-gully-response-${idx}`}
          className="mt-3 text-xs text-neutral-200 border border-neutral-800 bg-neutral-900/40 rounded p-2"
        >
          <div className="text-[10px] uppercase text-neutral-500 tracking-wider mb-1">
            Gully replied
          </div>
          {r.following_gully_response}
        </div>
      ) : null}

      <div className="mt-3 flex gap-2 flex-wrap">
        {r.fired ? (
          <button
            type="button"
            data-testid={`mini-aha-toggle-block-${idx}`}
            className="text-[10px] uppercase tracking-wider text-neutral-500 hover:text-neutral-300"
            onClick={() => setBlockOpen((v) => !v)}
          >
            {blockOpen ? "Hide insight block" : "Show insight block"}
          </button>
        ) : null}
        <button
          type="button"
          data-testid={`mini-aha-toggle-raw-${idx}`}
          className="text-[10px] uppercase tracking-wider text-neutral-500 hover:text-neutral-300"
          onClick={() => setRawOpen((v) => !v)}
        >
          {rawOpen ? "Hide raw JSON" : "Show raw JSON"}
        </button>
      </div>

      {blockOpen && r.prompt_block ? (
        <pre
          data-testid={`mini-aha-block-${idx}`}
          className="mt-2 text-[10px] leading-tight text-emerald-200 bg-neutral-900 rounded p-3 overflow-x-auto whitespace-pre-wrap"
        >
          {r.prompt_block}
        </pre>
      ) : null}

      {rawOpen ? (
        <pre
          data-testid={`mini-aha-raw-${idx}`}
          className="mt-2 text-[10px] leading-tight text-neutral-400 bg-neutral-900 rounded p-3 overflow-x-auto"
        >
          {JSON.stringify(r, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

export default function AdminMiniAhaTrace() {
  const [filterKind, setFilterKind] = useState("session_id");
  const [filterValue, setFilterValue] = useState("");
  const [ahaType, setAhaType] = useState("");
  const [firedState, setFiredState] = useState("");
  const [skipReason, setSkipReason] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [limit, setLimit] = useState(50);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Mini Aha Trace — Founder Debug";
  }, []);

  const fetchTrace = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const params = new URLSearchParams();
      if (filterValue.trim()) params.set(filterKind, filterValue.trim());
      if (ahaType) params.set("aha_type", ahaType);
      if (firedState) params.set("fired", firedState);
      if (skipReason) params.set("skip_reason", skipReason);
      if (since) params.set("since", since);
      if (until) params.set("until", until);
      params.set("limit", String(limit));
      const res = await api.get(
        `/admin/founder/mini-aha-trace?${params.toString()}`,
      );
      setData(res.data);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || e.message || String(e);
      setError(`${status || "err"}: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-mini-aha-trace-page"
      className="min-h-screen bg-neutral-950 text-neutral-100 p-6"
    >
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1
            data-testid="mini-aha-trace-title"
            className="text-2xl font-semibold"
          >
            Mini Aha Trace
          </h1>
          <p className="text-sm text-neutral-400 mt-1">
            Observational only. Every row is a decision the mini-aha engine
            already made — fired or skipped, with the substrate that
            triggered it and the adjacent conversation for context. Nothing
            on this panel changes production behaviour.
          </p>
        </div>

        <div
          data-testid="mini-aha-trace-filter-row"
          className="flex flex-wrap gap-2 mb-3 items-center"
        >
          <select
            data-testid="mini-aha-filter-kind"
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value)}
            className="bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm"
          >
            <option value="session_id">session_id</option>
            <option value="user_id">user_id</option>
            <option value="browser_id">browser_id</option>
          </select>
          <input
            data-testid="mini-aha-filter-value"
            type="text"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            placeholder="paste an id (optional)…"
            className="flex-1 min-w-[220px] bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm font-mono"
          />
        </div>

        <div
          data-testid="mini-aha-trace-filter-row-2"
          className="flex flex-wrap gap-2 mb-3 items-center"
        >
          <select
            data-testid="mini-aha-filter-aha-type"
            value={ahaType}
            onChange={(e) => setAhaType(e.target.value)}
            className="bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm"
          >
            {AHA_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <select
            data-testid="mini-aha-filter-fired"
            value={firedState}
            onChange={(e) => setFiredState(e.target.value)}
            className="bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm"
          >
            {FIRED_STATES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            data-testid="mini-aha-filter-skip-reason"
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            className="bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm"
          >
            {SKIP_REASONS.map((s) => (
              <option key={s} value={s}>
                {s || "any skip reason"}
              </option>
            ))}
          </select>
          <input
            data-testid="mini-aha-filter-limit"
            type="number"
            min={1}
            max={200}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value) || 50)}
            className="w-20 bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm"
          />
        </div>

        <div
          data-testid="mini-aha-trace-filter-row-3"
          className="flex flex-wrap gap-2 mb-6 items-center"
        >
          <label className="text-[11px] text-neutral-500 font-mono">
            since
            <input
              data-testid="mini-aha-filter-since"
              type="datetime-local"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="ml-2 bg-neutral-900 border border-neutral-800 rounded px-2 py-1 text-xs"
            />
          </label>
          <label className="text-[11px] text-neutral-500 font-mono">
            until
            <input
              data-testid="mini-aha-filter-until"
              type="datetime-local"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="ml-2 bg-neutral-900 border border-neutral-800 rounded px-2 py-1 text-xs"
            />
          </label>
          <button
            type="button"
            data-testid="mini-aha-fetch"
            onClick={fetchTrace}
            disabled={loading}
            className="ml-auto bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 rounded px-4 py-2 text-sm font-medium"
          >
            {loading ? "Loading…" : "Fetch trace"}
          </button>
        </div>

        {error ? (
          <div
            data-testid="mini-aha-trace-error"
            className="mb-4 border border-rose-900 bg-rose-950/30 text-rose-200 rounded p-3 text-sm"
          >
            {error}
          </div>
        ) : null}

        {data ? (
          <div
            data-testid="mini-aha-trace-summary"
            className="mb-4 border border-neutral-800 rounded p-3 text-xs text-neutral-400"
          >
            <div className="mb-1 font-mono">
              Generated {data.generated_at} · {data.n_rows} row
              {data.n_rows === 1 ? "" : "s"}
            </div>
            <div className="flex flex-wrap gap-3">
              <span>
                fired: <span className="text-emerald-300">{data.summary?.fired_count ?? 0}</span>
              </span>
              <span>
                skipped:{" "}
                <span className="text-neutral-300">
                  {data.summary?.skipped_count ?? 0}
                </span>
              </span>
              {Object.entries(data.summary?.by_aha_type || {}).map(([k, v]) => (
                <span key={k}>
                  {k}: <span className="text-neutral-200">{v}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {data && data.rows?.length ? (
          <div className="space-y-4" data-testid="mini-aha-trace-list">
            {data.rows.map((r, idx) => (
              <TraceCard key={`${r.fired_at}-${idx}`} r={r} idx={idx} />
            ))}
          </div>
        ) : null}

        {data && !data.rows?.length ? (
          <div
            data-testid="mini-aha-trace-empty"
            className="text-neutral-500 text-sm border border-neutral-800 rounded p-6 text-center"
          >
            No mini-aha events found for those filters.
          </div>
        ) : null}
      </div>
    </div>
  );
}
